import React from 'react';
import { AlertTriangle, Zap, Clock, ShieldCheck, Target } from 'lucide-react';

/**
 * <UrgencyChips>
 *
 * Compact signal pills surfacing Iter-133 intelligence:
 *   - urgency  { level, label, uplift_usd }
 *       level ∈ at_risk | urgent_fill_active | short_staffed_24h | normal
 *   - fill_confidence { level, label, score }
 *       level ∈ high | medium | low
 *
 * Props:
 *   urgency          — shape above (optional)
 *   fillConfidence   — shape above (optional)
 *   size?: 'xs' | 'sm'  default 'xs'
 *   showConfidenceOnNormal?: bool  default true
 */
const URGENCY_STYLES = {
  at_risk:            { cls: 'bg-rose-100 text-rose-800 border-rose-300',       Icon: AlertTriangle },
  urgent_fill_active: { cls: 'bg-amber-100 text-amber-800 border-amber-300',     Icon: Zap },
  short_staffed_24h:  { cls: 'bg-orange-100 text-orange-800 border-orange-300',  Icon: Clock },
};

const CONFIDENCE_STYLES = {
  high:   { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: ShieldCheck, label: 'High' },
  medium: { cls: 'bg-sky-100     text-sky-800     border-sky-300',     Icon: Target,      label: 'Med' },
  low:    { cls: 'bg-rose-100    text-rose-800    border-rose-300',    Icon: AlertTriangle, label: 'Low' },
};

const UrgencyChips = ({
  urgency,
  fillConfidence,
  size = 'xs',
  showConfidenceOnNormal = true,
}) => {
  const pad = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';
  const ico = size === 'sm' ? 'w-3 h-3' : 'w-2.5 h-2.5';

  const urgencyChip = urgency && URGENCY_STYLES[urgency.level] ? (() => {
    const { cls, Icon } = URGENCY_STYLES[urgency.level];
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border font-semibold uppercase tracking-wide ${pad} ${cls}`}
        data-testid={`urgency-chip-${urgency.level}`}
        title={urgency.label}
      >
        <Icon className={ico} /> {urgency.label || urgency.level.replace(/_/g, ' ')}
      </span>
    );
  })() : null;

  const showConf = fillConfidence && (showConfidenceOnNormal || !urgencyChip);
  const confChip = showConf && CONFIDENCE_STYLES[fillConfidence.level] ? (() => {
    const { cls, Icon, label } = CONFIDENCE_STYLES[fillConfidence.level];
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border font-semibold uppercase tracking-wide ${pad} ${cls}`}
        data-testid={`confidence-chip-${fillConfidence.level}`}
        title={`Fill confidence score: ${fillConfidence.score}`}
      >
        <Icon className={ico} /> Fill: {label}
        {typeof fillConfidence.score === 'number' && (
          <span className="opacity-70">&nbsp;· {Math.round(fillConfidence.score)}</span>
        )}
      </span>
    );
  })() : null;

  if (!urgencyChip && !confChip) return null;

  return (
    <div className="inline-flex items-center gap-1 flex-wrap" data-testid="urgency-chips">
      {urgencyChip}
      {confChip}
    </div>
  );
};

export default UrgencyChips;
